import { z } from "zod";
import { AppKeySchema } from "./entitlement";

export const AppConnectionCapabilitySchema = z
  .string()
  .trim()
  .min(3)
  .max(128)
  .regex(/^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)+$/);

export const AppConnectionScopeSchema = z.enum(["personal", "workspace"]);
export const AppConnectionStatusSchema = z.enum(["active", "revoked"]);

export const AppConnectionSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  sourceApp: AppKeySchema,
  targetApp: AppKeySchema,
  capability: AppConnectionCapabilitySchema,
  scope: AppConnectionScopeSchema,
  scopeId: z.string().min(1).max(128),
  status: AppConnectionStatusSchema,
  consentVersion: z.number().int().min(1),
  grantedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
  availability: z.enum(["ready", "source-unavailable", "target-unavailable", "workspace-access-denied"]),
});

export const AppConnectionListSchema = z.object({
  connections: z.array(AppConnectionSchema),
});

export const AppConnectionListQuerySchema = z.object({
  sourceApp: AppKeySchema.optional(),
  targetApp: AppKeySchema.optional(),
  status: AppConnectionStatusSchema.default("active"),
});

export const AppConnectionGrantRequestSchema = z
  .object({
    sourceApp: AppKeySchema,
    targetApp: AppKeySchema,
    capability: AppConnectionCapabilitySchema,
    scope: AppConnectionScopeSchema.default("personal"),
    scopeId: z.string().trim().min(1).max(128).optional(),
    consentVersion: z.number().int().min(1).default(1),
  })
  .superRefine((value, context) => {
    if (value.sourceApp === value.targetApp) {
      context.addIssue({
        code: "custom",
        message: "Source and target apps must be different",
        path: ["targetApp"],
      });
    }
    if (value.scope === "workspace" && !value.scopeId) {
      context.addIssue({
        code: "custom",
        message: "Workspace connections require a scopeId",
        path: ["scopeId"],
      });
    }
  });

export const AppConnectionParamsSchema = z.object({
  id: z.string().uuid(),
});

export type AppConnection = z.infer<typeof AppConnectionSchema>;
export type AppConnectionGrantRequest = z.infer<
  typeof AppConnectionGrantRequestSchema
>;
export type AppConnectionListQuery = z.infer<
  typeof AppConnectionListQuerySchema
>;
